import { useState, useEffect } from 'react'
import { Input, Textarea, NativeSelect, Button, VStack, Field, Dialog, CloseButton } from '@chakra-ui/react';
import { ResponsiveModal } from '../shared'
import type { CampaignDetailDto, CampaignStatus } from '../../types/campaign'
import type { ListRefreshStrategy } from '../../types/contactList'

interface CampaignFormModalProps {
  isOpen: boolean
  onClose: () => void
  campaign?: CampaignDetailDto
  onSubmit: (data: {
    label: string
    description?: string
    status?: CampaignStatus
    refreshStrategyOverride?: ListRefreshStrategy
  }) => void
  isLoading?: boolean
}

export function CampaignFormModal({ isOpen, onClose, campaign, onSubmit, isLoading }: CampaignFormModalProps) {
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<CampaignStatus>('Draft')
  const [refreshStrategyOverride, setRefreshStrategyOverride] = useState<string>('')

  useEffect(() => {
    if (campaign) {
      setLabel(campaign.label)
      setDescription(campaign.description ?? '')
      setStatus(campaign.status)
      setRefreshStrategyOverride(campaign.refreshStrategyOverride ?? '')
    } else {
      setLabel('')
      setDescription('')
      setStatus('Draft')
      setRefreshStrategyOverride('')
    }
  }, [campaign, isOpen])

  const handleSubmit = () => {
    if (!label.trim()) return
    onSubmit({
      label: label.trim(),
      description: description.trim() || undefined,
      refreshStrategyOverride: refreshStrategyOverride ? (refreshStrategyOverride as ListRefreshStrategy) : undefined,
      ...(campaign ? { status } : {}),
    })
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>{campaign ? 'Edit Campaign' : 'New Campaign'}</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4}>
              <Field.Root required>
                <Field.Label>Campaign Name</Field.Label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Spring 2026 Door Knock"
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>Description</Field.Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description..."
                  rows={3}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>Refresh Strategy Override</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={refreshStrategyOverride}
                    onChange={(e) => setRefreshStrategyOverride(e.target.value)}>
                    <option value="">None (use each list's own strategy)</option>
                    <option value="Manual">Manual (refresh on demand)</option>
                    <option value="AutoDaily">Auto Daily (refreshed overnight)</option>
                    <option value="Locked">Locked (frozen snapshot)</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                <Field.HelperText>
                  When set, creates campaign-scoped copies of referenced lists with this strategy.
                </Field.HelperText>
              </Field.Root>
              {campaign && (
                <Field.Root>
                  <Field.Label>Status</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={status}
                      onChange={(e) => setStatus(e.target.value as CampaignStatus)}>
                      <option value="Draft">Draft</option>
                      <option value="Active">Active</option>
                      <option value="Completed">Completed</option>
                      <option value="Archived">Archived</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              )}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!label.trim()}
            >
              {campaign ? 'Save Changes' : 'Create Campaign'}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
